import { Text, useDisclosure, useToast } from "@chakra-ui/react";
import { useCallback } from "react";
import { AiOutlineCloudUpload } from "react-icons/ai";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaButton, RanaModal } from "@nens/ranalib";

import { useEditPublication } from "../../../api/edit";
import {
  getId,
  getIsChanged,
  getIsPublishable,
  getPublishedVersion,
  getVersion,
} from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { useRemovePublicationThumbnail } from "../../map-thumbnail/api/remove-publication-thumbnail";

/* Button and modal to publish the current version of the publication */

export function PublishPublication() {
  const toast = useToast();
  const intl = useIntl();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const isPublishable = usePublicationStore(getIsPublishable);
  const publicationId = usePublicationStore(getId);
  const version = usePublicationStore(getVersion);
  const hasChanges = usePublicationStore(getIsChanged);
  const publishedVersion = usePublicationStore(getPublishedVersion);

  const { mutate: removeThumbnail } = useRemovePublicationThumbnail();

  const getDisabledTitle = () => {
    if (isPublishable) return undefined;
    if (hasChanges)
      return intl.formatMessage({
        id: "publication.publish.disabled.unsaved_changes",
        defaultMessage: "Save your changes before publishing",
      });
    if (version === 0)
      return intl.formatMessage({
        id: "publication.publish.disabled.no_version",
        defaultMessage: "Save a draft version first before publishing",
      });
    if (publishedVersion === version)
      return intl.formatMessage({
        id: "publication.publish.disabled.already_published",
        defaultMessage: "This version is already published",
      });
    return undefined;
  };

  const publishVersion = usePublicationStore((store) => store.publishVersion);

  const { mutate: editPublication } = useEditPublication();

  const doPublish = useCallback(() => {
    // First update publication through the API
    editPublication(
      {
        publicationId,
        publicationUpdate: {
          publishedVersion: version,
        },
      },
      {
        onSuccess: () => {
          // Then set in Zustand and close dialog
          publishVersion(version);
          removeThumbnail({ publicationId });
          toast({
            title: intl.formatMessage({
              id: "publication.publish_success",
              defaultMessage: "Publication published successfully",
            }),
            status: "success",
          });
          onClose();
        },
        onError: () => {
          toast({
            title: intl.formatMessage({
              id: "publication.publish_error",
              defaultMessage: "Something went wrong trying to publish the publication",
            }),
            status: "error",
          });
          onClose();
        },
      }
    );
  }, [
    editPublication,
    publicationId,
    version,
    publishVersion,
    removeThumbnail,
    toast,
    intl,
    onClose,
  ]);

  return (
    <>
      <RanaButton
        variant="primary"
        onClick={onOpen}
        isDisabled={!isPublishable}
        title={getDisabledTitle()}
        leftIcon={<AiOutlineCloudUpload />}
      >
        <FormattedMessage id="reference.publish_publication" defaultMessage="Publish draft" />
      </RanaButton>

      <RanaModal
        isOpen={isOpen}
        onClose={onClose}
        maxW="xl"
        buttons={[
          <RanaButton variant="secondary" onClick={onClose} key="cancel">
            <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
          </RanaButton>,
          <RanaButton variant="primary" onClick={doPublish} key="publish">
            <FormattedMessage id="button.publish" defaultMessage="Publish" />
          </RanaButton>,
        ]}
        topBar={
          <FormattedMessage id="publication.publish_title" defaultMessage="Publish publication" />
        }
      >
        <Text mb={4} maxW="prose">
          <FormattedMessage
            id="publication.publish_message"
            defaultMessage='This will publish the current draft version. All "Share publication" links, including those already made and shared previously, will show this version of the publication.'
          />
        </Text>
        <Text maxW="prose">
          <FormattedMessage
            id="publication.publish_confirm"
            defaultMessage="Do you want to publish this version?"
          />
        </Text>
      </RanaModal>
    </>
  );
}
